import { DateService, DocumentVisibilityTracker, Tab } from '@pulse/browser-shared';
import { PulseStorage, TrackerConfigurations, isToggleOn } from '@pulse/common';
import { ActivitySynchronizer } from '../activity.synchronizer';
import { ActivityTracker } from '../activity.tracker';

export class TabInfoService {
    private tabId?: string;
    private unloaded = false;
    private persisted = false;
    private lastActivity: number;

    constructor(
        private persistentStorage: PulseStorage,
        private visibilityService: DocumentVisibilityTracker,
        private activityTracker: ActivityTracker,
        private activitySynchronizer: ActivitySynchronizer,
        private date: DateService,
        private config: TrackerConfigurations,
    ) {
        const now = date.now();
        this.lastActivity = now;
        this.persistentStorage.freeSpace$.subscribe(() => {
            this.cleanup();
        });
    }

    startService(tabId: string) {
        this.tabId = tabId;
        this.setupActivityTracker();
        this.updateTabInfo();
        this.cleanup();
    }

    fetchSiblingTabs(id = ''): Tab[] {
        if (!isToggleOn(this.config.eventEnrichment, 'tab_id')) {
            return [];
        }
        if (!this.tabId) {
            throw new Error('Tab identifier is not available!');
        }

        const tabId = this.tabId;

        return this.persistentStorage
            .keys()
            .filter((key) => key.startsWith(`tab_info_${id}`) && key.indexOf(tabId) === -1)
            .map((key) => this.persistentStorage.getJSON<Tab>(key))
            .filter(Boolean) as Tab[];
    }

    getCurrentTab(): Tab {
        if (!this.tabId) {
            throw new Error('Tab identifier is not available!');
        }

        const tab = {
            id: this.tabId,
            lastActivity: this.lastActivity,
            persisted: this.persisted,
            unloaded: this.unloaded,
            visible: this.visibilityService.visible,
        };

        return tab;
    }

    private updateTabInfo() {
        const tab = this.getCurrentTab();
        this.persistentStorage.setJSON(`tab_info_${tab.id}`, tab);
    }

    private cleanup() {
        const now = this.date.now();
        const tabs = this.fetchSiblingTabs().filter(
            (t) => now - t.lastActivity > this.config.tabOpenerDetectionTimeWindow,
        );

        for (const tab of tabs) {
            this.persistentStorage.remove(`tab_info_${tab.id}`);
            this.activitySynchronizer.remove(tab.id);
        }
    }

    private setupActivityTracker(): void {
        this.visibilityService.legacyPageHide().subscribe((e) => {
            this.unloaded = true;
            this.persisted = (e as PageTransitionEvent).persisted;
            this.updateTabInfo();
        });

        this.visibilityService.legacyOnUnload().subscribe(() => {
            this.unloaded = true;
            this.updateTabInfo();
        });

        this.activityTracker.interactions().subscribe(() => {
            this.lastActivity = this.date.now();
            this.updateTabInfo();
        });
    }
}
